<script setup>
import { useLayout } from "src/composables/useLayout";
import { $t, updatePreset, updateSurfacePalette } from "@primevue/themes";
import { ref, onMounted, watch, onBeforeMount } from "vue";
import { LAYOUT_SURFACES_CONFIG } from "src/utils/layout-config";
import SelectButton from "primevue/selectbutton";

const {
  layoutConfig,
  setPrimary,
  setSurface,
  isDarkTheme,
  setScale,
  setTableSize,
  setDarkMode,
  setTheme,
  systemDarkMode,
} = useLayout();

const scales = ref([8, 10, 12, 13, 14, 15, 16, 18, 20]);

function updateColors(type, color) {
  if (type === "primary") {
    setPrimary(color.name);
  } else if (type === "surface") {
    setSurface(color.name);
  }

  applyTheme(type, color);
}

function applyTheme(type, color) {
  if (type === "primary") {
    updatePreset(getPresetExt(color));
  } else if (type === "surface") {
    updateSurfacePalette(color.palette);
  }
}

function handleClickSurface(surface) {
  setSurface(surface.name);
}

watch(
  layoutConfig,
  () => {
    updateColors(
      "surface",
      LAYOUT_SURFACES_CONFIG.find((s) => s.name === layoutConfig.value.surface)
    );
  },
  { deep: true, immediate: true }
);

const decrementScale = () => {
  setScale(layoutConfig.value.scale - 1);
  applyScale();
};

const incrementScale = () => {
  setScale(layoutConfig.value.scale + 1);
  applyScale();
};

const onClickScale = (scale) => {
  setScale(scale);
  applyScale();
};

const applyScale = () => {
  document.documentElement.style.fontSize = `${layoutConfig.value.scale}px`;
};

const handleThemeChange = (theme) => {
  setTheme(theme);
  if (theme === "system") {
    setDarkMode(systemDarkMode.matches);
  } else {
    setDarkMode(theme === "dark");
  }
};

// Handle system theme changes
const handleSystemThemeChange = (e) => {
  if (layoutConfig.value.theme === "system") {
    setDarkMode(e.matches);
  }
};

onBeforeMount(() => {
  systemDarkMode.removeEventListener("change", handleSystemThemeChange);
});

onMounted(() => {
  setScale(layoutConfig.value.scale);
  applyScale();
  setTableSize(layoutConfig.value.tableSize);

  // Add system theme change listener
  systemDarkMode.addEventListener("change", handleSystemThemeChange);

  // Initialize theme
  if (layoutConfig.value.theme === "system") {
    setDarkMode(systemDarkMode.matches);
  } else {
    setDarkMode(layoutConfig.value.theme === "dark");
  }
});
</script>

<template>
  <div
    class="config-panel hidden absolute top-[3.25rem] right-0 w-64 p-4 bg-surface-0 dark:bg-surface-900 border border-surface rounded-border origin-top shadow-[0px_3px_5px_rgba(0,0,0,0.02),0px_0px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.08)]"
  >
    <div class="flex flex-col gap-4">
      <div>
        <span class="text-sm text-muted-color font-semibold">Surface</span>
        <div class="pt-2 flex gap-2 flex-wrap justify-between">
          <button
            v-for="surface of LAYOUT_SURFACES_CONFIG"
            :key="surface.name"
            type="button"
            :title="surface.name"
            @click="handleClickSurface(surface)"
            :class="[
              'border-none w-5 h-5 rounded-full p-0 cursor-pointer outline-none outline-offset-1',
              {
                'outline-primary': layoutConfig.surface
                  ? layoutConfig.surface === surface.name
                  : isDarkTheme
                  ? surface.name === 'zinc'
                  : surface.name === 'slate',
              },
            ]"
            :style="{ backgroundColor: `${surface.palette['500']}` }"
          ></button>
        </div>
      </div>

      <div>
        <span class="text-sm text-muted-color font-semibold">Font Scale</span>
        <div class="flex align-items-center pt-2">
          <div class="flex gap-2 align-items-center">
            <i
              v-for="s in scales"
              :key="s"
              class="pi pi-circle-fill text-300 cursor-pointer"
              :class="{ 'text-primary-500': s === layoutConfig.scale }"
              @click="onClickScale(s)"
            />
          </div>
        </div>
      </div>

      <div>
        <span class="text-sm text-muted-color font-semibold">Table Density</span>
        <div class="pt-2">
          <SelectButton
            :model-value="layoutConfig.tableSize"
            :options="['small', 'normal', 'large']"
            aria-labelledby="basic"
            @update:model-value="setTableSize($event)"
          />
        </div>
      </div>

      <div>
        <span class="text-sm text-muted-color font-semibold">Theme</span>
        <div class="pt-2">
          <SelectButton
            :model-value="layoutConfig.theme"
            :options="['system', 'dark', 'light']"
            aria-labelledby="basic"
            @update:model-value="handleThemeChange($event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
